import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { X, Check } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const promoCache = new Map();
const fetchPromo = (key) => {
  if (!promoCache.has(key)) {
    promoCache.set(key, axios.get(`${API}/payments/affirm-promo?amount=${key}`).then(r => r.data).catch(() => null));
  }
  return promoCache.get(key);
};

export function AffirmCalculatorModal({ initialPrice, lang = "en", onClose, getPrequalUrl }) {
  const es = lang === "es";
  const [amountStr, setAmountStr] = useState(String(Math.round(initialPrice * 100) / 100));
  const amount = useMemo(() => {
    const n = parseFloat(String(amountStr).replace(/[^0-9.]/g, ""));
    return isNaN(n) ? 0 : Math.min(n, 30000);
  }, [amountStr]);
  const [promo, setPromo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!amount || amount < 50) { setPromo(null); return; }
    const key = Math.round(amount);
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      fetchPromo(key).then(d => { if (alive) { setPromo(d); setLoading(false); } });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [amount]);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="affirm-calculator-modal">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-[#0FA0EA] rounded-t-2xl px-6 py-5 relative">
          <button onClick={onClose} className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full text-white transition-colors" data-testid="affirm-modal-close" aria-label="Close"><X size={18} /></button>
          <span className="text-white text-2xl font-black tracking-tight lowercase">affirm</span>
          <p className="text-white/90 text-sm font-semibold mt-1">
            {es ? "Compra ahora, paga con el tiempo" : "Buy now, pay over time"}
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Editable purchase amount */}
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">{es ? "Monto de compra" : "Purchase amount"}</label>
            <div className="mt-1.5 flex items-center border-2 border-gray-200 rounded-xl px-4 py-2.5 focus-within:border-[#0FA0EA] transition-colors">
              <span className="text-lg font-bold text-gray-400 mr-1">$</span>
              <input
                type="text" inputMode="decimal" value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                className="flex-1 text-lg font-bold text-[#1A202C] outline-none bg-transparent min-w-0"
                data-testid="affirm-modal-amount-input"
              />
            </div>
            <p className="text-[11px] text-gray-400 mt-1">{es ? "Ajusta el monto — los precios vienen directamente de Affirm" : "Adjust the amount — pricing comes straight from Affirm"}</p>
          </div>

          {/* Live Affirm offer */}
          {amount < 50 ? (
            <p className="text-sm text-gray-500 text-center py-4" data-testid="affirm-modal-min-notice">
              {es ? "Affirm está disponible en compras de $50 o más." : "Affirm is available on purchases of $50 or more."}
            </p>
          ) : (
            <div className="space-y-2" data-testid="affirm-modal-plans">
              {loading && !promo && (
                <p className="text-xs text-gray-400 text-center py-3" data-testid="affirm-modal-loading">{es ? "Obteniendo tarifas de Affirm..." : "Getting live rates from Affirm..."}</p>
              )}
              {promo?.pay_in_4 && (
                <div className="flex items-center justify-between rounded-xl border-2 border-gray-100 px-4 py-3" data-testid="affirm-modal-plan-pay4">
                  <div>
                    <p className="text-lg font-bold text-[#1A202C]">${Math.ceil(amount / 4).toLocaleString()}<span className="text-xs font-normal text-gray-500"> ×4</span></p>
                    <p className="text-[11px] text-gray-500">{es ? "4 pagos sin interés, cada 2 semanas" : "4 interest-free payments, every 2 weeks"}</p>
                  </div>
                  <p className="text-xs font-semibold text-[#0FA0EA]">{es ? "0% interés" : "0% interest"}</p>
                </div>
              )}
              {promo?.monthly && (
                <div className="relative flex items-center justify-between rounded-xl border-2 border-[#0FA0EA] bg-blue-50/50 px-4 py-3" data-testid="affirm-modal-plan-monthly">
                  {promo.zero_apr && (
                    <span className="absolute -top-2 right-3 bg-[#0FA0EA] text-white text-[9px] font-bold px-2 py-px rounded-full">{es ? "0% APR DISPONIBLE" : "0% APR AVAILABLE"}</span>
                  )}
                  <div>
                    <p className="text-lg font-bold text-[#1A202C]">${promo.monthly.toLocaleString()}<span className="text-xs font-normal text-gray-500">/{es ? "mes" : "mo"}</span></p>
                    <p className="text-[11px] text-gray-500">{es ? "Estimación \u201Cdesde\u201D de Affirm para clientes que califican" : "Affirm's \u201Cas low as\u201D estimate for qualified customers"}</p>
                  </div>
                </div>
              )}
              {!loading && promo && !promo.monthly && !promo.pay_in_4 && (
                <p className="text-sm text-gray-500 text-center py-3" data-testid="affirm-modal-fallback">
                  {es ? "Affirm te mostrará tus opciones exactas al precalificar." : "Affirm will show your exact options when you pre-qualify."}
                </p>
              )}
            </div>
          )}

          {/* Trust note */}
          <div className="flex items-start gap-2 bg-green-50 rounded-lg p-2.5">
            <Check size={14} className="text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-700">
              {es
                ? <><span className="font-semibold">Verificar tu elegibilidad toma ~30 segundos</span> y no afecta tu puntaje de crédito.</>
                : <><span className="font-semibold">Checking your eligibility takes ~30 seconds</span> and won't affect your credit score.</>}
            </p>
          </div>

          {/* CTA */}
          <a
            href={getPrequalUrl(amount >= 50 ? amount : undefined)}
            target="_blank" rel="noreferrer"
            className="flex items-center justify-center gap-1.5 w-full bg-[#0FA0EA] hover:bg-[#0C8AC9] text-white text-sm font-bold px-5 py-3 rounded-full shadow-sm transition-colors"
            data-testid="affirm-modal-prequalify-btn"
          >
            {es ? "Ve si calificas con Affirm" : "See if you qualify with Affirm"} →
          </a>

          {/* Disclosures — Affirm-required compliance language */}
          <p className="text-[10px] text-gray-400 leading-snug" data-testid="affirm-modal-disclosure">
            {es
              ? "Los montos mostrados son estimaciones proporcionadas por Affirm. Las opciones de pago a través de Affirm están sujetas a una verificación de elegibilidad, pueden no estar disponibles en todos los estados, y son proporcionadas por estos socios prestamistas: affirm.com/lenders. Las opciones dependen del monto de tu compra y puede requerirse un pago inicial. Tus términos reales pueden variar de 0–36% APR según elegibilidad y son determinados por Affirm al momento de pagar. CA residentes: Préstamos hechos o gestionados por Affirm Loan Services, LLC bajo licencia de California Financing Law."
              : "Payment amounts shown are estimates provided by Affirm. Payment options through Affirm are subject to an eligibility check, may not be available in all states, and are provided by these lending partners: affirm.com/lenders. Options depend on your purchase amount, and a down payment may be required. Your actual terms may vary from 0–36% APR based on eligibility and are determined by Affirm at checkout. CA residents: Loans by Affirm Loan Services, LLC are made or arranged pursuant to a California Financing Law license."}
          </p>
        </div>
      </motion.div>
    </div>
  );
}
